import React from 'react';
import { 
  View, 
  StyleSheet, 
  TouchableOpacity, 
  Animated, 
  Dimensions, 
  TouchableWithoutFeedback,
  StatusBar
} from 'react-native';
import { Text, Surface, Divider } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LoadingImage from './LoadingImage';
import { RootStackParamList } from '../types/navigation';

type SlidingMenuProps = {
  isVisible: boolean;
  onClose: () => void;
};

const { width } = Dimensions.get('window');

const SlidingMenu: React.FC<SlidingMenuProps> = ({ isVisible, onClose }) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const slideAnim = React.useRef(new Animated.Value(width)).current;

  React.useEffect(() => {
    if (isVisible) {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: width,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [isVisible, slideAnim]);

  const handleNavigateToHome = () => {
    onClose();
    navigation.navigate('Home');
  };

  const handleNavigateToHelpFeed = () => {
    onClose();
    if (navigation.canGoBack()) {
      navigation.navigate('HelpFeed');
    } else {
      navigation.replace('HelpFeed');
    }
  };

  const handleNavigateToFeedback = () => {
    onClose();
    navigation.navigate('Feedback');
  };

  const handleNavigateToProfileSettings = () => {
    onClose();
    navigation.navigate('ProfileSettings');
  };

  const handleNavigateToResources = () => {
    onClose();
    navigation.navigate('Resources');
  };

  if (!isVisible) return null;

  return (
    <View style={styles.container}>
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.backdrop} />
      </TouchableWithoutFeedback>
      
      <Animated.View 
        style={[
          styles.menuContainer,
          { transform: [{ translateX: slideAnim }] }
        ]}
      >
        <Surface style={styles.menu}>
          <View style={styles.menuHeader}>
            <LoadingImage 
              source={require('../../assets/logo-transparent.png')}
              style={styles.headerLogo}
              contentFit="contain"
              transition={200}
              cachePolicy="memory-disk"
            />
            <View style={styles.headerRight}>
              <Text style={styles.menuTitle}>Menu</Text>
              <TouchableOpacity onPress={onClose}>
                <Text style={styles.closeButton}>✕</Text>
              </TouchableOpacity>
            </View>
          </View>
          
          <Divider style={styles.divider} />
          
          <TouchableOpacity 
            style={styles.menuItem}
            onPress={handleNavigateToHelpFeed}
          >
            <Text style={styles.menuItemText}>Help Others</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.menuItem}
            onPress={handleNavigateToResources}
          >
            <Text style={styles.menuItemText}>Resources</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.menuItem}
            onPress={handleNavigateToFeedback}
          >
            <Text style={styles.menuItemText}>Help Us Improve</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.menuItem}
            onPress={handleNavigateToProfileSettings}
          >
            <Text style={styles.menuItemText}>Profile Settings</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.menuItem}
            onPress={handleNavigateToHome}
          >
            <Text style={styles.menuItemText}>Return Home</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.menuItem, styles.inactiveMenuItem]}
            onPress={handleNavigateToHome}
            disabled={true}
          >
            <Text style={[styles.menuItemText, styles.inactiveMenuItemText]}>Workspace (Coming soon)</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.menuItem, styles.inactiveMenuItem]}
            onPress={handleNavigateToHome}
            disabled={true}
          >
            <Text style={[styles.menuItemText, styles.inactiveMenuItemText]}>Teachers' Forum (Coming soon)</Text>
          </TouchableOpacity>
          
          {/* Add more menu items here as needed */}
        </Surface>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  menuContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: width * 0.8,
    maxWidth: 350,
  },
  menu: {
    flex: 1,
    backgroundColor: 'white',
    elevation: 5,
  },
  menuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
  },
  headerLogo: {
    width: 75,
    height: 75,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  menuTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    fontSize: 24,
    color: '#666',
    fontWeight: 'bold',
  },
  divider: {
    marginVertical: 10,
  },
  menuItem: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  menuItemText: {
    fontSize: 16,
    color: '#333',
  },
  inactiveMenuItem: {
    opacity: 0.5,
  },
  inactiveMenuItemText: {
    color: '#999',
  },
});

export default SlidingMenu; 